import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { LineChart, Eye, MousePointerClick, Percent, Users, Target, DollarSign, Lightbulb, ArrowRight, FlaskConical } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useDemo } from "@/components/demo/DemoUi";
import {
  DEMO_CAMPAIGN_SCENARIO, DEMO_CAMPAIGN_REPORT, DEMO_CAMPAIGN_UI, SIMULATION_BANNER, DEMO_REPORT_ANCHOR, loc,
} from "@/config/demoCampaignScenario";

const nf = new Intl.NumberFormat("fr-FR");
const fmtInt = (v) => nf.format(Math.round(v));
const fmtPct = (v) => `${(v * 100).toFixed(1).replace(".", ",")} %`;
const fmtMoney = (v, cur) => `${nf.format(Math.round(v * 100) / 100)} ${cur || ""}`.trim();

function SimulationBanner({ text }) {
  return (
    <div
      data-testid="demo-report-simulation-banner"
      role="note"
      className="flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      <FlaskConical size={16} className="mt-0.5 shrink-0 text-amber-600" />
      <p><strong className="font-semibold">Simulation.</strong> {text}</p>
    </div>
  );
}

function Metric({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        <Icon size={12} /> {label}
      </p>
      <p className="mt-1 font-heading text-xl font-bold text-[#17201A]">{value}</p>
    </div>
  );
}

// Read-only presentational simulated report for the demo France -> Mauritius
// campaign. Visible ONLY in demo context.
export default function DemoCampaignReport() {
  const { isDemo } = useDemo();
  const { lang } = useLang();
  const location = useLocation();
  const ref = useRef(null);

  useEffect(() => {
    if (isDemo && ref.current && location.hash === `#${DEMO_REPORT_ANCHOR}`) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isDemo, location.hash]);

  if (!isDemo) return null;
  const t = DEMO_CAMPAIGN_UI[lang] || DEMO_CAMPAIGN_UI.fr;
  const s = DEMO_CAMPAIGN_SCENARIO;
  const r = DEMO_CAMPAIGN_REPORT;
  const m = r.metrics;
  const progressPct = Math.round((m.conversions / r.objectiveTarget) * 100);

  return (
    <section
      id={DEMO_REPORT_ANCHOR}
      ref={ref}
      data-testid="demo-campaign-report"
      className="scroll-mt-24 space-y-4 rounded-2xl border border-indigo-200 bg-indigo-50/50 p-5 shadow-sm"
      aria-labelledby="demo-report-title"
    >
      <SimulationBanner text={loc(SIMULATION_BANNER, lang)} />

      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3D2B] to-[#2C5540]">
          <LineChart size={18} className="text-[#5C9873]" />
        </div>
        <div>
          <h2 id="demo-report-title" className="font-heading text-lg font-bold tracking-tight text-[#17201A]">
            {t.reportCardTitle}
          </h2>
          <p className="text-sm text-[#64748B]">{loc(s.name, lang)} · {loc(s.period, lang)}</p>
        </div>
      </div>

      {/* Results grid */}
      <div>
        <h3 className="mb-2 text-sm font-bold text-[#17201A]">{t.resultsTitle}</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="demo-report-metrics">
          <Metric icon={Eye} label={t.reach} value={fmtInt(m.reach)} testid="demo-report-reach" />
          <Metric icon={Eye} label={t.impressions} value={fmtInt(m.impressions)} testid="demo-report-impressions" />
          <Metric icon={MousePointerClick} label={t.clicks} value={fmtInt(m.clicks)} testid="demo-report-clicks" />
          <Metric icon={Percent} label={t.ctr} value={fmtPct(m.ctr)} testid="demo-report-ctr" />
          <Metric icon={Users} label={t.leads} value={fmtInt(m.leads)} testid="demo-report-leads" />
          <Metric icon={Target} label={t.conversions} value={fmtInt(m.conversions)} testid="demo-report-conversions" />
          <Metric icon={DollarSign} label={t.costPerResult} value={fmtMoney(m.costPerResult, s.currency)} testid="demo-report-cost-per-result" />
        </div>
      </div>

      {/* Progress vs objective */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="demo-report-progress">
        <div className="mb-1.5 flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            <Target size={12} /> {t.progressTitle}
          </p>
          <span className="text-sm font-bold text-[#3E7256]" data-testid="demo-report-progress-pct">{progressPct} %</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-[#3E7256]" style={{ width: `${Math.min(100, progressPct)}%` }} />
        </div>
        <p className="mt-1.5 text-xs text-slate-500">
          {fmtInt(m.conversions)} {t.progressLabel} {fmtInt(r.objectiveTarget)}.
        </p>
      </div>

      {/* Key learnings + next action */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="demo-report-learnings">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            <Lightbulb size={12} /> {t.learningsTitle}
          </p>
          <ul className="mt-2 space-y-2">
            {r.learnings.map((l, i) => (
              <li key={i} data-testid={`demo-report-learning-${i}`} className="flex items-start gap-2 text-sm text-[#475569]">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#3E7256]" />
                <span>{loc(l, lang)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="demo-report-next-action">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-600">
            <ArrowRight size={12} /> {t.nextActionTitle}
          </p>
          <p className="mt-2 text-sm text-[#334155]">{loc(r.nextAction, lang)}</p>
        </div>
      </div>
    </section>
  );
}
